import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import { TenantUserModel, PermissionTypeEnum, PermissionModel, CategoryModel, CategoryUserModel, RoleModel } from '@/libs/Api';
import { categoriesStore } from '@/libs/categories/+state/store';
import { rolesStore } from '@/libs/roles/+state/store';
import { CrudGetter } from './+state/models/crud-getter';

/**
 * Helper třída pro kontrolu oprávnění s podporou kontextových oprávnění dle kategorií
 */
export class PermissionsHelper {
    /**
     * Kontroluje, zda má uživatel přístup ke kategorii a zda má požadovaná oprávnění pro tuto kategorii
     * @param categoryId ID kategorie
     * @param requiredPermissions Oprávnění nutná pro přístup (ANY - stačí jedno z nich)
     * @returns true pokud má uživatel přístup
     */
    static hasAccessToCategory(
        categoryId: string | null | undefined,
        requiredPermissions?: PermissionTypeEnum[]
    ): boolean {
        if (!categoryId) return false;

        const tenantUser = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
        if (!tenantUser) return false;

        // A1: Přístup ke všem kategoriím
        if (tenantUser.accessAllCategories) {
            // Bez požadavků = má přístup
            if (!requiredPermissions?.length) return true;
            // S požadavky = kontroluj globální oprávnění
            return this.hasPermissions(requiredPermissions);
        }

        // A2: Přístup jen k vybraným kategoriím
        const categoryAccess = tenantUser.categories?.find((c) => c.categoryId === categoryId);
        if (!categoryAccess) return false;

        // Bez požadavků = má přístup
        if (!requiredPermissions?.length) return true;

        // A2.1: Zdědované role - použij globální oprávnění
        if (categoryAccess.inheritedRoles) {
            return this.hasPermissions(requiredPermissions);
        }

        // A2.2: Vlastní role - kontroluj oprávnění z categoryUserRoles
        const categoryPermissions = this.getCategoryPermissions(categoryAccess);

        // ANY match - stačí jedno z požadovaných oprávnění
        return requiredPermissions.some((rp) => categoryPermissions.includes(rp));
    }

    /**
     * KLÍČOVÁ FUNKCE - vrátí ID kategorií, ke kterým má uživatel přístup S danými oprávněními
     * @param requiredPermissions Oprávnění nutná pro zahrnutí kategorie (ANY - stačí jedno)
     * @returns Array ID kategorií, které splňují požadavky
     */
    static getUserAccessibleCategoryIds(requiredPermissions?: PermissionTypeEnum[]): string[] {
        const tenantUser = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
        if (!tenantUser) return [];

        const allCategories = (categoriesStore.useGetter(CrudGetter.Data) as CategoryModel[]) ?? [];

        // A1: Přístup ke všem kategoriím
        if (tenantUser.accessAllCategories) {
            // Bez požadavků = všechny kategorie
            if (!requiredPermissions?.length) {
                return allCategories.map((c) => c.id!);
            }

            // S požadavky = kontroluj, zda má globální oprávnění
            if (this.hasPermissions(requiredPermissions)) {
                return allCategories.map((c) => c.id!);
            }
            return [];
        }

        // A2: Přístup jen k vybraným kategoriím
        if (!tenantUser.categories?.length) return [];

        // Bez požadavků = všechny povolené kategorie
        if (!requiredPermissions?.length) {
            return tenantUser.categories.map((c) => c.categoryId!);
        }

        // S požadavky = filtruj podle oprávnění
        const accessible: string[] = [];

        tenantUser.categories.forEach((categoryAccess) => {
            // A2.1: Zdědované role
            if (categoryAccess.inheritedRoles) {
                if (this.hasPermissions(requiredPermissions)) {
                    accessible.push(categoryAccess.categoryId!);
                }
            }
            // A2.2: Vlastní role
            else {
                const categoryPermissions = this.getCategoryPermissions(categoryAccess);

                // ANY match
                if (requiredPermissions.some((rp) => categoryPermissions.includes(rp))) {
                    accessible.push(categoryAccess.categoryId!);
                }
            }
        });

        return accessible;
    }

    /**
     * Vrátí všechna oprávnění pro danou kategorii (z vlastních rolí)
     * @param categoryAccess CategoryUserModel
     * @returns Array oprávnění
     */
    private static getCategoryPermissions(categoryAccess: CategoryUserModel): PermissionTypeEnum[] {
        const perms: PermissionTypeEnum[] = [];
        const roles = rolesStore.useGetter(CrudGetter.Data) as RoleModel[];
        
        categoryAccess.categoryUserRoles?.forEach((cur) => {
            // Najít roli v rolesStore podle roleId (server neposílá expanded role)
            const role = roles.find(r => r.id === cur.roleId);
            if (role?.permissions) {
                role.permissions.forEach((p: PermissionModel) => {
                    if (p.permissionType && !perms.includes(p.permissionType)) {
                        perms.push(p.permissionType);
                    }
                });
            }
        });
        return perms;
    }

    /**
     * Kontroluje, zda má uživatel alespoň jedno z požadovaných oprávnění (ANY)
     * @param permissionTypes Array oprávnění
     * @returns true pokud má alespoň jedno
     */
    private static hasPermissions(permissionTypes: PermissionTypeEnum[]): boolean {
        if (!permissionTypes?.length) return true;
        const permissions = authStore.useGetter(AuthGetter.GetPermissions) as PermissionTypeEnum[];
        return permissions?.any((x: any) => permissionTypes.any((p: any) => p == x)) ?? false;
    }
}

